/*
 * Delivery week picker (Gifting v2, plan G1.2/G1.3).
 *
 * Only does anything on templates where the "gift_message_group" block has enable_week_picker turned on
 * (default false). Resolves the live gift-weeks App Proxy path with the same candidate-probe pattern
 * bmb-builder.js's shipcalc popup already uses for /apps/f2c-delivery (base, then -1, -2, -3, first
 * candidate that answers wins, cached in localStorage so repeat visits skip straight to it), because the
 * f2c-delivery App Proxy subpath collided in production and the working route is currently suffixed
 * (/apps/f2c-delivery-1/gift-weeks as of the 2026-08-25 backend deploy). Populates the select, and wires
 * two hidden inputs so the add-to-cart form submits "Delivery week" (human label) and "_delivery_week"
 * (monday_iso) only when a real week is chosen. No property is submitted for the default "Next available
 * delivery" option. On probe failure across every candidate, timeout (3s per attempt), or an empty weeks
 * list, the picker silently collapses to just that default option: no error text, no fabricated dates.
 */
(function () {
  var FETCH_TIMEOUT_MS = 3000;
  var STORAGE_KEY = 'f2c-gift-weeks-proxy-path';

  function clearProperty(input) {
    if (!input) return;
    input.removeAttribute('name');
    input.value = '';
  }

  function setProperty(input, name, value) {
    if (!input) return;
    input.setAttribute('name', name);
    input.value = value;
  }

  function buildHolidayLabel(week) {
    if (week.holidayName) {
      return week.label + ' (unavailable: ' + week.holidayName + ')';
    }
    return week.label + ' (unavailable)';
  }

  function populateWeeks(select, weeks) {
    weeks.forEach(function (week) {
      if (!week || !week.monday_iso || !week.label) return;

      var option = document.createElement('option');
      option.value = week.monday_iso;

      if (week.isHolidayWeek) {
        option.disabled = true;
        option.textContent = buildHolidayLabel(week);
      } else {
        option.textContent = week.label;
      }

      select.appendChild(option);
    });
  }

  function readCachedPath() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeCachedPath(path) {
    try {
      window.localStorage.setItem(STORAGE_KEY, path);
    } catch (e) {
      // localStorage unavailable (private mode, quota, disabled): resolution just re-probes next load.
    }
  }

  function candidatePaths(base) {
    var list = [base];
    for (var i = 1; i <= 3; i++) {
      var p = base + '-' + i;
      if (list.indexOf(p) === -1) list.push(p);
    }

    var cached = readCachedPath();
    if (cached && list.indexOf(cached) > 0) {
      list.splice(list.indexOf(cached), 1);
      list.unshift(cached);
    }

    return list;
  }

  function probeCandidate(base, weeksParam) {
    var controller = 'AbortController' in window ? new AbortController() : null;
    var timeoutId = window.setTimeout(function () {
      if (controller) controller.abort();
    }, FETCH_TIMEOUT_MS);

    var requestUrl = base + '/gift-weeks?weeks=' + encodeURIComponent(weeksParam);

    return fetch(requestUrl, {
      signal: controller ? controller.signal : undefined,
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        window.clearTimeout(timeoutId);
        if (!response.ok) {
          throw new Error('gift-weeks probe failed with status ' + response.status);
        }
        return response.json();
      })
      .then(function (data) {
        var weeks = data && Array.isArray(data.weeks) ? data.weeks : null;
        if (!weeks) throw new Error('gift-weeks probe returned no weeks array');
        return { base: base, weeks: weeks };
      })
      .catch(function (err) {
        window.clearTimeout(timeoutId);
        throw err;
      });
  }

  function discoverWeeks(base, weeksParam) {
    var candidates = candidatePaths(base);
    var idx = 0;

    function tryNext() {
      if (idx >= candidates.length) return Promise.reject(new Error('no gift-weeks proxy path found'));
      var candidate = candidates[idx++];
      return probeCandidate(candidate, weeksParam).catch(tryNext);
    }

    return tryNext().then(function (result) {
      writeCachedPath(result.base);
      return result;
    });
  }

  function setupPicker(root) {
    var select = root.querySelector('[data-gift-week-select]');
    var labelInput = root.querySelector('[data-gift-week-label-input]');
    var isoInput = root.querySelector('[data-gift-week-iso-input]');

    if (!select || !labelInput || !isoInput) return;

    var endpoint = root.getAttribute('data-endpoint');
    var weeksParam = root.getAttribute('data-weeks') || '8';

    function applySelection() {
      var option = select.options[select.selectedIndex];

      if (!option || option.value === '') {
        clearProperty(labelInput);
        clearProperty(isoInput);
        return;
      }

      setProperty(labelInput, 'properties[Delivery week]', option.textContent.trim());
      setProperty(isoInput, 'properties[_delivery_week]', option.value);
    }

    select.addEventListener('change', applySelection);
    clearProperty(labelInput);
    clearProperty(isoInput);

    if (!endpoint) return;

    // data-endpoint carries the full base-candidate path, e.g. "/apps/f2c-delivery/gift-weeks";
    // strip the trailing "/gift-weeks" so candidatePaths() can build -1/-2/-3 variants of the app path.
    var base = endpoint.replace(/\/gift-weeks\/?$/, '');

    var loadingOption = document.createElement('option');
    loadingOption.textContent = 'Loading available weeks...';
    loadingOption.disabled = true;
    loadingOption.setAttribute('data-gift-week-loading', '');
    select.appendChild(loadingOption);

    discoverWeeks(base, weeksParam)
      .then(function (result) {
        var loading = select.querySelector('[data-gift-week-loading]');
        if (loading) loading.remove();

        populateWeeks(select, result.weeks);
      })
      .catch(function () {
        var loading = select.querySelector('[data-gift-week-loading]');
        if (loading) loading.remove();

        // Silent fallback: only "Next available delivery" remains selectable. No error text, no
        // fabricated dates, per the plan's acceptance checks.
      });
  }

  function init() {
    document.querySelectorAll('[data-gift-week-picker]').forEach(setupPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
